import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form } from 'antd';

import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import type { PriceDifferentiationModeType } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

type PriceButtonsProps = {
  priceDifferentiationMode: PriceDifferentiationModeType;
  disableNightToggle?: boolean;
};

const getModeMap = () => ({
  nightDay: {
    [PRICE_DIFFERENTIATION_MODE.NONE]: PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME,
    [PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME]: PRICE_DIFFERENTIATION_MODE.NONE,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD]:
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME]:
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD,
  },
  adultChild: {
    [PRICE_DIFFERENTIATION_MODE.NONE]: PRICE_DIFFERENTIATION_MODE.WITH_CHILD,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD]: PRICE_DIFFERENTIATION_MODE.NONE,
    [PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME]:
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME]:
      PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME,
  },
});

export const PriceButtons = ({
  priceDifferentiationMode,
  disableNightToggle = false,
}: PriceButtonsProps) => {
  const form = Form.useFormInstance();
  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );
  const allowedPax = Form.useWatch(['allowedPaxTypes'], form);

  const hasAdultOnly =
    allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.ADULT;
  const hasChildOnly =
    allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.CHILD;
  const hasBothPaxTypes = !hasAdultOnly && !hasChildOnly;

  const hasDayPrice =
    priceDifferentiationMode ===
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME ||
    priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME;

  const hasAdultChildPrice =
    hasBothPaxTypes &&
    (priceDifferentiationMode ===
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME ||
      priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD);

  const handleTogglePriceDifferentiation = (
    buttonType: 'nightDay' | 'adultChild'
  ) => {
    const modeMap = getModeMap();
    const newMode = modeMap[buttonType][priceDifferentiationMode];

    form.setFieldsValue({
      openingConfiguration: {
        priceConfiguration: {
          priceDifferentiationMode: newMode,
        },
      },
    });
  };

  return (
    <Form.Item
      name={[
        'openingConfiguration',
        'priceConfiguration',
        'priceDifferentiationMode',
      ]}
      noStyle
      initialValue={PRICE_DIFFERENTIATION_MODE.NONE}
    >
      <div className="price-configuration-buttons">
        {!isAlwaysIncludedWithNight && (
          <Button
            icon={hasDayPrice ? <DeleteOutlined /> : <PlusOutlined />}
            onClick={() => handleTogglePriceDifferentiation('nightDay')}
            type={hasDayPrice ? 'dashed' : 'default'}
            danger={hasDayPrice}
            disabled={disableNightToggle}
          >
            {`${hasDayPrice ? 'Delete' : 'Add'} night/day price`}
          </Button>
        )}

        {hasBothPaxTypes && (
          <Button
            icon={hasAdultChildPrice ? <DeleteOutlined /> : <PlusOutlined />}
            onClick={() => handleTogglePriceDifferentiation('adultChild')}
            type={hasAdultChildPrice ? 'dashed' : 'default'}
            danger={hasAdultChildPrice}
          >
            {`${hasAdultChildPrice ? 'Delete' : 'Add'} adult/child price`}
          </Button>
        )}
      </div>
    </Form.Item>
  );
};
